/*
 * Copyright (c) 2024 Anvilor Solutions LLC - All Rights Reserved.
 *
 * The information and source code contained herein is the exclusive property of
 * Anvilor Solutions LLC and may not be disclosed, examined, or reproduced in whole
 * or in part without the explicit written authorization from Anvilor Solutions LLC.
 */

import { Injectable } from "@angular/core";
import { Inject } from "@angular/core";
import { Observable } from "rxjs";
import { BYPASS_ORG_HEADER } from "@anvilor/ngx-apps-core";
import { API_BASE } from "@anvilor/ngx-apps-core";
import { ObjectId } from "@anvilor/ngx-apps-core";
import { HttpClient } from "@angular/common/http";
import { HttpContext } from "@angular/common/http";
import { map } from "rxjs/operators";
import { MatDialog } from "@angular/material/dialog";
import { ENVIRONMENT_CONFIG } from "../../../../environments/environment-config";
import { EvironmentConfig } from "../../../../environments/environment-config";
import { ResponseEditConfig } from "../../../models/submission-edit";
import { ResponseData } from "../../../models/submission-edit";
import { AuthenticatedForResponseData } from "../../../models/submission-edit";
import { DavCredentialsDialogComponent } from "./dav-dialog";
import { DavCredentialsDialogData } from "./dav-dialog";

export interface LeaseInfo {
  access_code: string;
  is_sms: boolean;
  is_password: boolean;
}

@Injectable({ providedIn: "root" })
export class AnvilorFormsApiDavService {
  constructor(
    @Inject(ENVIRONMENT_CONFIG) private env: EvironmentConfig,
    private http: HttpClient
  ) {}

  postMakeLease(key: string, user: string): Observable<LeaseInfo> {
    return this.http.post<LeaseInfo>(
      "/anvilor_forms/dav/make_lease",
      { key, user },
      {
        context: new HttpContext()
          .set(BYPASS_ORG_HEADER, true)
          .set(API_BASE, this.env.apiBase),
      }
    );
  }

  postGetResponseData(params: {
    response_oid: string | ObjectId;
    access_code: string;
    document_password?: string;
    sms_code?: string;
  }): Observable<ResponseData> {
    return this.http.post<ResponseData>(
      "/anvilor_forms/dav/get_response_data",
      params,
      {
        context: new HttpContext()
          .set(BYPASS_ORG_HEADER, true)
          .set(API_BASE, this.env.apiBase),
      }
    );
  }
}

@Injectable()
export class DavSubmissionAuthenticator {
  constructor(private davApi: AnvilorFormsApiDavService, private dialog: MatDialog) {}

  authenticateAndGetData(
    editConfig: ResponseEditConfig
  ): Observable<AuthenticatedForResponseData> {
    return this.launchDialogForDavAuthentication(editConfig).pipe(
      map(({ lease, response_data }) => ({
        submission_auth_header: `AnvilorDav ${lease.access_code}`,
        response_data,
      }))
    );
  }

  private launchDialogForDavAuthentication(
    edit_config: ResponseEditConfig
  ): Observable<{ lease: LeaseInfo; response_data: ResponseData }> {
    const ref = this.dialog.open<
      DavCredentialsDialogComponent,
      DavCredentialsDialogData,
      { lease: LeaseInfo; response_data: ResponseData }
    >(DavCredentialsDialogComponent, {
      disableClose: true,
      data: {
        dav_key: edit_config.auth_data.key!,
        dav_user: edit_config.auth_data.user!,
        response_oid: edit_config.response_oid!,
      },
    });
    return ref.afterClosed() as Observable<{
      lease: LeaseInfo;
      response_data: ResponseData;
    }>;
  }
}
